import {EXPENSE_CATEGORIES, ExpenseCategory} from "./expenseLabels";

/**
 * The filter of the expense invoice list as it is kept in the URL, so other views can link to a
 * filtered list. Query parameters:
 *
 * - supplier: supplier identifier
 * - category: an ExpenseCategory value, or NONE for invoices without a category
 * - month: service month as YYYY-MM
 * - status: PROPOSED or CONFIRMED, or NONE for invoices without a service period
 * - q: search term (invoice number or supplier)
 * - deleted=1: also invoices deleted in FastBill
 */
export const NO_VALUE = 'NONE';

export const SERVICE_PERIOD_STATUSES = ['PROPOSED', 'CONFIRMED'] as const;
export type ServicePeriodStatus = typeof SERVICE_PERIOD_STATUSES[number];

export type ExpenseInvoiceListFilter = {
    supplier?: string
    category?: ExpenseCategory | typeof NO_VALUE
    month?: string
    status?: ServicePeriodStatus | typeof NO_VALUE
    q?: string
    deleted?: boolean
};

export const EXPENSE_INVOICES_PATH = '/invoices/incoming';

/**
 * Whether the value is a complete service month as YYYY-MM.
 */
export function isServiceMonth(value: string): boolean {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/**
 * Reads the filter from the query parameters and ignores values it does not know.
 */
export function expenseInvoiceFilterFromSearchParams(searchParams: URLSearchParams): ExpenseInvoiceListFilter {
    const category = searchParams.get('category');
    const status = searchParams.get('status');
    const month = searchParams.get('month');
    return {
        supplier: searchParams.get('supplier') || undefined,
        category: category === NO_VALUE || (EXPENSE_CATEGORIES as readonly string[]).includes(category ?? '') ? category as ExpenseInvoiceListFilter['category'] : undefined,
        month: month && isServiceMonth(month) ? month : undefined,
        status: status === NO_VALUE || (SERVICE_PERIOD_STATUSES as readonly string[]).includes(status ?? '') ? status as ExpenseInvoiceListFilter['status'] : undefined,
        q: searchParams.get('q')?.trim() || undefined,
        deleted: searchParams.get('deleted') === '1',
    };
}

export function expenseInvoiceFilterToSearchParams(filter: ExpenseInvoiceListFilter): URLSearchParams {
    const searchParams = new URLSearchParams();
    filter.supplier && searchParams.set('supplier', filter.supplier);
    filter.category && searchParams.set('category', filter.category);
    filter.month && searchParams.set('month', filter.month);
    filter.status && searchParams.set('status', filter.status);
    filter.q && searchParams.set('q', filter.q);
    filter.deleted && searchParams.set('deleted', '1');
    return searchParams;
}

/**
 * The path of the invoice list showing the given filter, e.g. for the drill-down of the overview.
 */
export function expenseInvoicesPath(filter: ExpenseInvoiceListFilter = {}): string {
    const query = expenseInvoiceFilterToSearchParams(filter).toString();
    return EXPENSE_INVOICES_PATH + (query !== '' ? '?' + query : '');
}

/**
 * The filter as the ExpenseInvoiceFilter input of the expenseInvoices query.
 */
export function expenseInvoiceFilterToGraphQL(filter: ExpenseInvoiceListFilter) {
    return {
        supplierIdentifier: filter.supplier ?? null,
        expenseCategory: filter.category && filter.category !== NO_VALUE ? filter.category : null,
        withoutExpenseCategory: filter.category === NO_VALUE,
        serviceMonth: filter.month ?? null,
        servicePeriodStatus: filter.status && filter.status !== NO_VALUE ? filter.status : null,
        withoutServicePeriod: filter.status === NO_VALUE,
        searchTerm: filter.q ?? null,
        includeDeleted: filter.deleted === true,
    };
}
